/**
 * Custom Header — Clas Håkansson
 * Handles: dropdown, flyout, cart drawer, search modal, sticky header,
 * header height CSS vars, country selector, focus trapping.
 */

class CustomHeader extends HTMLElement {
  constructor() {
    super();

    // Elements
    this.backdrop = this.querySelector('[data-backdrop]');
    this.dropdownPairs = []; // populated in connectedCallback
    this.flyout = this.querySelector('[data-flyout]');
    this.cartDrawer = this.querySelector('[data-cart-drawer]');
    this.searchDrawer = this.querySelector('[data-search-drawer]');
    this.cartCount = this.querySelector('[data-cart-count]');
    this.searchInput = this.querySelector('[data-search-input]');
    this.headerSectionEl = this.closest('.shopify-section');

    // State
    this.activeOverlay = null;
    this.searchDebounce = null;

    // Bind methods
    this.handleClick = this.handleClick.bind(this);
    this.handleKeydown = this.handleKeydown.bind(this);
    this.handleDropdownEnter = this.handleDropdownEnter.bind(this);
    this.handleDropdownLeave = this.handleDropdownLeave.bind(this);
    this.handleCartUpdate = this.handleCartUpdate.bind(this);
    this.handleOutsideClick = this.handleOutsideClick.bind(this);
  }

  // HTML escape helper to prevent XSS
  escapeHtml(str) {
    const div = document.createElement('div');
    div.textContent = str;
    return div.innerHTML;
  }

  connectedCallback() {
    // Event listeners
    this.addEventListener('click', this.handleClick);
    document.addEventListener('keydown', this.handleKeydown);
    document.addEventListener('cart:update', this.handleCartUpdate);

    // Build dropdown pairs (trigger ↔ dropdown via aria-controls) and bind hover handlers
    this.querySelectorAll('[data-action="toggle-dropdown"]').forEach((trigger) => {
      const id = trigger.getAttribute('aria-controls');
      if (!id) return;
      const dropdown = this.querySelector('#' + CSS.escape(id));
      if (!dropdown) return;
      const pair = { trigger, dropdown };
      this.dropdownPairs.push(pair);
      const enter = () => this.handleDropdownEnter(pair);
      const leave = () => this.handleDropdownLeave(pair);
      trigger.addEventListener('mouseenter', enter);
      trigger.addEventListener('mouseleave', leave);
      dropdown.addEventListener('mouseenter', enter);
      dropdown.addEventListener('mouseleave', leave);
    });

    // Search input
    if (this.searchInput) {
      this.searchInput.addEventListener('input', () => this.handleSearchInput());
    }

    // Country selector outside click (bound method for cleanup)
    document.addEventListener('click', this.handleOutsideClick);

    // Header height CSS variables
    this.updateHeaderHeight();
    this.resizeObserver = new ResizeObserver(() => this.updateHeaderHeight());
    this.resizeObserver.observe(this);

    // Scroll direction detection for compact header
    this.lastScrollY = window.scrollY;
    this.scrollTicking = false;
    this.scrollCooldown = false;
    this.isCompact = false;
    this.handleScroll = this.handleScroll.bind(this);
    window.addEventListener('scroll', this.handleScroll, { passive: true });

    // Initial cart load
    this.loadCart();
  }

  disconnectedCallback() {
    document.removeEventListener('keydown', this.handleKeydown);
    document.removeEventListener('cart:update', this.handleCartUpdate);
    document.removeEventListener('click', this.handleOutsideClick);
    window.removeEventListener('scroll', this.handleScroll);
    if (this.resizeObserver) this.resizeObserver.disconnect();
  }

  // ---------------------------------------------------------------------------
  // Compact header on scroll
  // ---------------------------------------------------------------------------
  handleScroll() {
    if (this.scrollTicking || this.scrollCooldown) return;
    this.scrollTicking = true;
    requestAnimationFrame(() => {
      const currentY = window.scrollY;
      const delta = currentY - this.lastScrollY;
      const headerGroup = document.querySelector('#header-group');

      if (Math.abs(delta) > 10 && headerGroup) {
        let shouldCompact;

        if (currentY <= 10) {
          shouldCompact = false;
        } else if (delta > 0) {
          shouldCompact = true;
        } else {
          shouldCompact = false;
        }

        // Only act if state actually changes
        if (shouldCompact !== this.isCompact) {
          this.isCompact = shouldCompact;
          if (shouldCompact) {
            headerGroup.classList.add('ch-header-compact');
          } else {
            headerGroup.classList.remove('ch-header-compact');
          }

          // Cooldown: ignore scroll events for 400ms after a state change
          this.scrollCooldown = true;
          setTimeout(() => { this.scrollCooldown = false; }, 400);
        }

        this.lastScrollY = currentY;
      }

      this.scrollTicking = false;
    });
  }

  handleOutsideClick(e) {
    const countryDropdown = this.querySelector('.ch-country__dropdown--open');
    if (countryDropdown && !e.target.closest('[data-country-selector]')) {
      countryDropdown.classList.remove('ch-country__dropdown--open');
      const trigger = this.querySelector('[data-action="toggle-country"]');
      if (trigger) trigger.setAttribute('aria-expanded', 'false');
    }
  }

  // ---------------------------------------------------------------------------
  // Header Height CSS Variables
  // ---------------------------------------------------------------------------
  updateHeaderHeight() {
    const height = this.offsetHeight;
    document.body.style.setProperty('--header-height', `${height}px`);
    // Single-row header: top-row-height equals full header height
    document.body.style.setProperty('--top-row-height', `${height}px`);

    const headerGroup = document.querySelector('#header-group');
    if (headerGroup) {
      let groupHeight = 0;
      for (const child of headerGroup.children) {
        if (child === this.headerSectionEl || child.contains(this) || !(child instanceof HTMLElement)) continue;
        groupHeight += child.offsetHeight;
      }
      document.body.style.setProperty('--header-group-height', `${groupHeight}px`);
    }
    document.body.style.setProperty('--transparent-header-offset-boolean', '0');
  }

  // ---------------------------------------------------------------------------
  // Click Handler (event delegation)
  // ---------------------------------------------------------------------------
  handleClick(e) {
    const action = e.target.closest('[data-action]');
    if (!action) {
      // Check backdrop click
      if (e.target === this.backdrop) {
        this.closeAll();
      }
      return;
    }

    const actionName = action.dataset.action;
    switch (actionName) {
      case 'open-flyout':
        this.openOverlay('flyout');
        break;
      case 'open-cart':
        this.openOverlay('cart');
        break;
      case 'open-search':
        this.openOverlay('search');
        break;
      case 'close-flyout':
        this.closeAll();
        break;
      case 'close-cart':
        this.closeAll();
        break;
      case 'close-search':
        this.closeAll();
        break;
      case 'toggle-dropdown': {
        e.preventDefault();
        const pair = this.dropdownPairs.find((p) => p.trigger === action);
        if (!pair) break;
        if (pair.dropdown.classList.contains('ch-dropdown--open')) {
          this.closeDropdown(pair);
        } else {
          this.openDropdown(pair);
        }
        break;
      }
      case 'toggle-country':
        e.preventDefault();
        const countryDd = this.querySelector('.ch-country__dropdown');
        if (countryDd) {
          countryDd.classList.toggle('ch-country__dropdown--open');
          action.setAttribute('aria-expanded', countryDd.classList.contains('ch-country__dropdown--open'));
        }
        break;
      case 'cart-qty-minus':
        this.updateCartItem(action.dataset.key, parseInt(action.dataset.qty) - 1);
        break;
      case 'cart-qty-plus':
        this.updateCartItem(action.dataset.key, parseInt(action.dataset.qty) + 1);
        break;
      case 'cart-remove':
        this.updateCartItem(action.dataset.key, 0);
        break;
    }
  }

  // ---------------------------------------------------------------------------
  // Keyboard Handler
  // ---------------------------------------------------------------------------
  handleKeydown(e) {
    if (e.key === 'Escape') {
      // Close country dropdown
      const countryDd = this.querySelector('.ch-country__dropdown--open');
      if (countryDd) {
        countryDd.classList.remove('ch-country__dropdown--open');
        const trigger = this.querySelector('[data-action="toggle-country"]');
        if (trigger) trigger.setAttribute('aria-expanded', 'false');
        return;
      }
      const openPair = this.dropdownPairs.find((p) => p.dropdown.classList.contains('ch-dropdown--open'));
      if (openPair) {
        this.closeDropdown(openPair);
      }
      if (this.activeOverlay) {
        this.closeAll();
      }
    }
  }

  // ---------------------------------------------------------------------------
  // Dropdown (hover)
  // ---------------------------------------------------------------------------
  handleDropdownEnter(pair) {
    clearTimeout(this.dropdownTimeout);
    this.openDropdown(pair);
  }

  handleDropdownLeave(pair) {
    this.dropdownTimeout = setTimeout(() => this.closeDropdown(pair), 150);
  }

  openDropdown(pair) {
    if (!pair) return;
    // Close any other open dropdown first
    this.dropdownPairs.forEach((p) => {
      if (p !== pair && p.dropdown.classList.contains('ch-dropdown--open')) {
        this.closeDropdown(p);
      }
    });
    pair.dropdown.classList.add('ch-dropdown--open');
    pair.trigger.setAttribute('aria-expanded', 'true');
  }

  closeDropdown(pair) {
    if (!pair) return;
    pair.dropdown.classList.remove('ch-dropdown--open');
    pair.trigger.setAttribute('aria-expanded', 'false');
  }

  // ---------------------------------------------------------------------------
  // Overlay Management (flyout, cart, search)
  // ---------------------------------------------------------------------------
  openOverlay(type) {
    this.closeAll();
    this.activeOverlay = type;

    this.backdrop.classList.add('ch-backdrop--visible');
    document.body.style.overflow = 'hidden';

    switch (type) {
      case 'flyout':
        this.flyout?.classList.add('ch-flyout--open');
        this.querySelector('[data-action="open-flyout"]')?.setAttribute('aria-expanded', 'true');
        this.trapFocus(this.flyout);
        break;
      case 'cart':
        this.cartDrawer?.classList.add('ch-cart--open');
        this.trapFocus(this.cartDrawer);
        break;
      case 'search':
        this.searchDrawer?.classList.add('ch-search--open');
        this.trapFocus(this.searchDrawer);
        setTimeout(() => this.searchInput?.focus(), 300);
        break;
    }
  }

  closeAll() {
    this.activeOverlay = null;
    this.backdrop?.classList.remove('ch-backdrop--visible');
    document.body.style.overflow = '';

    this.flyout?.classList.remove('ch-flyout--open');
    this.cartDrawer?.classList.remove('ch-cart--open');
    this.searchDrawer?.classList.remove('ch-search--open');

    this.querySelector('[data-action="open-flyout"]')?.setAttribute('aria-expanded', 'false');
    this.releaseFocus();
  }

  // ---------------------------------------------------------------------------
  // Focus Trapping
  // ---------------------------------------------------------------------------
  trapFocus(container) {
    if (!container) return;
    this._focusTrap = container;
    this._previousFocus = document.activeElement;

    const focusable = container.querySelectorAll(
      'a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])'
    );
    if (focusable.length === 0) return;

    this._firstFocusable = focusable[0];
    this._lastFocusable = focusable[focusable.length - 1];

    this._trapHandler = (e) => {
      if (e.key !== 'Tab') return;
      if (e.shiftKey) {
        if (document.activeElement === this._firstFocusable) {
          e.preventDefault();
          this._lastFocusable.focus();
        }
      } else {
        if (document.activeElement === this._lastFocusable) {
          e.preventDefault();
          this._firstFocusable.focus();
        }
      }
    };

    container.addEventListener('keydown', this._trapHandler);
    this._firstFocusable.focus();
  }

  releaseFocus() {
    if (this._focusTrap && this._trapHandler) {
      this._focusTrap.removeEventListener('keydown', this._trapHandler);
    }
    this._previousFocus?.focus();
    this._focusTrap = null;
  }

  // ---------------------------------------------------------------------------
  // Cart (AJAX)
  // ---------------------------------------------------------------------------
  async loadCart() {
    try {
      const res = await fetch('/cart.js', { credentials: 'same-origin' });
      if (!res.ok) throw new Error(`Cart API error: ${res.status}`);
      const cart = await res.json();
      this.renderCart(cart);
    } catch (e) {
      console.error('Failed to load cart:', e);
    }
  }

  async updateCartItem(key, quantity) {
    try {
      const res = await fetch('/cart/change.js', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: key, quantity }),
      });
      if (!res.ok) throw new Error(`Cart API error: ${res.status}`);
      const cart = await res.json();
      this.renderCart(cart);
    } catch (e) {
      console.error('Failed to update cart:', e);
    }
  }

  handleCartUpdate(e) {
    // Fabric dispatches cart:update after add-to-cart
    this.loadCart();
  }

  renderCart(cart) {
    // Update count badge
    const countEl = this.querySelector('[data-cart-count]');
    if (countEl) countEl.textContent = cart.item_count;

    // Update total
    const totalEl = this.querySelector('[data-cart-total]');
    if (totalEl) totalEl.textContent = this.formatMoney(cart.total_price);

    // Free shipping bar
    const freeShipQty = parseInt(this.cartDrawer?.dataset.freeShippingQty || 3);
    const needsMore = this.cartDrawer?.querySelectorAll('[data-shipping-needs-more]');
    const freeEl = this.cartDrawer?.querySelector('[data-shipping-free]');
    const leftEl = this.cartDrawer?.querySelector('[data-shipping-left]');
    const progressEl = this.cartDrawer?.querySelector('[data-shipping-progress]');

    if (cart.item_count >= freeShipQty) {
      needsMore?.forEach(el => el.style.display = 'none');
      if (freeEl) freeEl.style.display = '';
    } else {
      needsMore?.forEach(el => el.style.display = '');
      if (freeEl) freeEl.style.display = 'none';
      if (leftEl) leftEl.textContent = freeShipQty - cart.item_count;
      if (progressEl) progressEl.style.width = `${(cart.item_count / freeShipQty) * 100}%`;
    }

    // Cart items
    const itemsContainer = this.cartDrawer?.querySelector('[data-cart-items]');
    const footerEl = this.cartDrawer?.querySelector('[data-cart-footer]');
    const emptyEl = this.cartDrawer?.querySelector('[data-cart-empty]');

    if (cart.item_count === 0) {
      if (itemsContainer) itemsContainer.innerHTML = '';
      if (footerEl) footerEl.style.display = 'none';
      if (emptyEl) emptyEl.style.display = '';
      needsMore?.forEach(el => el.style.display = 'none');
      if (freeEl) freeEl.style.display = 'none';
    } else {
      if (footerEl) footerEl.style.display = '';
      if (emptyEl) emptyEl.style.display = 'none';
      if (itemsContainer) {
        itemsContainer.innerHTML = cart.items.map(item => `
          <div class="ch-cart__item">
            <img class="ch-cart__item-image" src="${item.image ? this.escapeHtml(item.image.replace(/(\.[^.]+)$/, '_160x$1')) : ''}" alt="${this.escapeHtml(item.title)}" loading="lazy">
            <div class="ch-cart__item-info">
              <a href="${this.escapeHtml(item.url)}" class="ch-cart__item-title">${this.escapeHtml(item.product_title)}</a>
              <div class="ch-cart__item-price">${this.formatMoney(item.final_line_price)}</div>
              <div class="ch-cart__item-qty">
                <button data-action="cart-qty-minus" data-key="${this.escapeHtml(item.key)}" data-qty="${item.quantity}" aria-label="Decrease quantity">-</button>
                <span>${item.quantity}</span>
                <button data-action="cart-qty-plus" data-key="${this.escapeHtml(item.key)}" data-qty="${item.quantity}" aria-label="Increase quantity">+</button>
              </div>
            </div>
          </div>
        `).join('');
      }
    }
  }

  formatMoney(cents) {
    // Use Shopify's built-in formatMoney if available
    if (window.Shopify?.formatMoney) {
      return window.Shopify.formatMoney(cents);
    }
    // Fallback: use money_format from data attribute or default
    const format = this.dataset.moneyFormat || '€{{amount_with_comma_separator}}';
    const amount = (cents / 100).toFixed(2);
    const amountWithComma = amount.replace('.', ',');
    return format
      .replace('{{amount_with_comma_separator}}', amountWithComma)
      .replace('{{amount}}', amount);
  }

  // ---------------------------------------------------------------------------
  // Predictive Search
  // ---------------------------------------------------------------------------
  handleSearchInput() {
    clearTimeout(this.searchDebounce);
    const query = this.searchInput.value.trim();

    const noQueryEl = this.searchDrawer.querySelector('[data-search-no-query]');
    const withQueryEl = this.searchDrawer.querySelector('[data-search-with-query]');

    if (!query) {
      noQueryEl.style.display = '';
      withQueryEl.style.display = 'none';
      return;
    }

    this.searchDebounce = setTimeout(async () => {
      try {
        const res = await fetch(
          `/search/suggest.json?q=${encodeURIComponent(query)}&resources[type]=product&resources[limit]=10`,
          { credentials: 'same-origin' }
        );
        const data = await res.json();
        this.renderSearchResults(data, query);
      } catch (e) {
        console.error('Search failed:', e);
      }
    }, 300);
  }

  renderSearchResults(data, query) {
    const noQueryEl = this.searchDrawer.querySelector('[data-search-no-query]');
    const withQueryEl = this.searchDrawer.querySelector('[data-search-with-query]');
    const productsGrid = this.searchDrawer.querySelector('[data-search-products]');
    const emptyEl = this.searchDrawer.querySelector('[data-search-empty]');
    const allLink = this.searchDrawer.querySelector('[data-search-all-link]');

    noQueryEl.style.display = 'none';
    withQueryEl.style.display = '';

    const products = data.resources?.results?.products || [];

    if (products.length === 0) {
      productsGrid.innerHTML = '';
      emptyEl.style.display = '';
      allLink.style.display = 'none';
      return;
    }

    emptyEl.style.display = 'none';
    allLink.style.display = '';
    allLink.href = `/search?q=${encodeURIComponent(query)}`;

    productsGrid.innerHTML = products.map(product => {
      const image = product.featured_image?.url || product.image || '';
      const price = product.price || '';
      return `
        <a href="${this.escapeHtml(product.url)}" class="ch-search__product">
          <img class="ch-search__product-image" src="${this.escapeHtml(image)}" alt="${this.escapeHtml(product.title)}" loading="lazy">
          <div class="ch-search__product-title">${this.escapeHtml(product.title)}</div>
          <div class="ch-search__product-price">${this.escapeHtml(price)}</div>
        </a>
      `;
    }).join('');
  }
}

customElements.define('custom-header', CustomHeader);
